import { snakeCase } from "lodash";
import { CompanyRole } from "../../../auth/AuthProvider";
import { TPagingResponse } from "../../../models/PagingResponse";
import { ApiError } from "../../../network/mainClient/apiError";
import { request } from "../../../network/mainClient/request";
import { TCompany, TCompanyWithRole } from "../../company/models";
import { TCompanyRole } from "../../company/network";
import { getCompaniesFromIds } from "../../company/utils";
import {
  mapLinkedVerificationRequestIn,
  mapVerificationRequestIn,
} from "../mappers";
import {
  TComplianceComment,
  TDocumentInfo,
  TExternalProviderID,
  THistoryResponse,
  TLinkedVerificationRequest,
  TLinkedVerificationRequestResponse,
  TScreeningEvent,
  TUserInfo,
  TVerificationRequestAccess,
  TVerificationRequest,
} from "../models";
import { VerificationStatus } from "../utils/getStatus";

export * from "./documents";

const qe = (v: string | number): string => encodeURIComponent(String(v));

const FILE_UPLOAD_TOO_LARGE_MESSAGE = "The file you attached is too large";

const throwResponseError = (response: {
  apiError?: ApiError;
  errorMessage?: string;
  statusCode: number;
}): never => {
  if (response.apiError) {
    throw response.apiError;
  }

  if (response.statusCode === 413) {
    throw new ApiError({
      code: 413,
      reason: "FILE_UPLOAD_TOO_LARGE",
      domain: "id.vnx.li",
      message: FILE_UPLOAD_TOO_LARGE_MESSAGE,
    });
  }

  throw new Error(response.errorMessage);
};

export type THistoryFileDownloadResponse = {
  downloadUrl: string;
};

const buildVerificationRequestListQuery = (
  limit: number,
  offset: number,
  statuses: string[],
  query: string,
  category: string
) => {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });

  statuses.forEach((status) => params.append("statuses", status));

  if (query) {
    params.append("query", query);
  }

  if (category) {
    params.append("category", category);
  }

  return params.toString();
};

export const getVerificationRequestListForCompany = async (
  limit = 10,
  offset = 0,
  companyId: string,
  statuses: string[] = [],
  query = "",
  category = ""
): Promise<TPagingResponse<TVerificationRequest>> => {
  const queryString = buildVerificationRequestListQuery(
    limit,
    offset,
    statuses,
    query,
    category
  );
  const response = await request({
    path: `/api/v1/verification-service/verification-request/company/${qe(companyId)}?${queryString}`,
    method: "GET",
  });

  if (response.ok) {
    return {
      count: parseInt(response.data.count),
      items: response.data.verificationRequest.map((x: TVerificationRequest) =>
        mapVerificationRequestIn(x)
      ),
    };
  }

  throw new Error(response.errorMessage);
};

export const getVerificationRequestList = async (
  limit = 10,
  offset = 0,
  statuses: string[] = [],
  query = "",
  category = ""
): Promise<TPagingResponse<TVerificationRequest>> => {
  const queryString = buildVerificationRequestListQuery(
    limit,
    offset,
    statuses,
    query,
    category
  );
  const response = await request({
    path: `/api/v1/verification-service/verification-request?${queryString}`,
    method: "GET",
  });

  if (response.ok) {
    return {
      count: parseInt(response.data.count),
      items: response.data.verificationRequest.map((x: TVerificationRequest) =>
        mapVerificationRequestIn(x)
      ),
    };
  }

  throw new Error(response.errorMessage);
};

export const getVerificationRequest = async (
  id: string
): Promise<TVerificationRequest> => {
  const response = await request({
    path: `/api/v1/verification-service/verification-request/${qe(id)}`,
    method: "GET",
  });

  if (response.ok) {
    return mapVerificationRequestIn(response.data.verificationRequest);
  }

  throw new Error(response.errorMessage);
};

export const getVerificationRequestAccess = async (
  verificationRequestId: string,
): Promise<TVerificationRequestAccess> => {
  const response = await request({
    path: `/api/v1/verification-service/verification-request/${qe(verificationRequestId)}/access`,
    method: "GET",
  });

  if (response.ok) {
    return response.data as TVerificationRequestAccess;
  }

  throw new Error(response.errorMessage);
};

export const getLinkedVerificationRequests = async (
  verificationRequestId: string
): Promise<TLinkedVerificationRequest[]> => {
  const response = await request({
    path: `/api/v1/verification-service/verification-request/${qe(verificationRequestId)}/links`,
    method: "GET",
  });

  if (response.ok) {
    return (response.data.links ?? []).map((link: TLinkedVerificationRequestResponse) =>
      mapLinkedVerificationRequestIn(link)
    );
  }

  throw new Error(response.errorMessage);
};

export type WidgetLinkPurpose = "user_notification";

export const getWidgetLink = async (
  verificationRequestId: string,
  purpose?: WidgetLinkPurpose
): Promise<{ link: string }> => {
  const queryString = purpose
    ? `?${new URLSearchParams({ purpose }).toString()}`
    : "";
  const response = await request({
    path: `/api/v1/verification-service/verification-request/${qe(
      verificationRequestId
    )}/widget-link${queryString}`,
    method: "GET",
  });

  if (response.ok) {
    return { link: response.data.link ?? "" };
  }

  throw new Error(response.errorMessage);
};

export const getKybWidgetLink = async (
  individualVrId: number
): Promise<{ link: string }> => {
  const response = await request({
    path: `/api/v1/verification-service/kyb/individual-vr/${qe(individualVrId)}/widget-link`,
    method: "GET",
  });

  if (response.ok) {
    return { link: response.data.link ?? "" };
  }

  throw new Error(response.errorMessage);
};

export const sendKybInvite = async (individualVrId: number): Promise<void> => {
  const response = await request({
    path: `/api/v1/verification-service/kyb/individual-vr/${qe(individualVrId)}/send-invite`,
    method: "POST",
  });

  if (response.ok) {
    return;
  }

  throw new Error(response.errorMessage);
};

export const sendKybAllInvites = async (
  entityVrId: string
): Promise<void> => {
  const response = await request({
    path: `/api/v1/verification-service/kyb/entity-vr/${qe(entityVrId)}/send-all-invites`,
    method: "POST",
  });

  if (response.ok) {
    return;
  }

  throw new Error(response.errorMessage);
};

export const uploadDocumentData = async (
  documentId: string,
  documentType: string,
  data: any,
  verificationRequestId?: string,
): Promise<TVerificationRequest> => {
  const response = await request({
    path: `/api/v1/verification-service/document/${qe(documentId)}`,
    method: "POST",
    body: {
      verificationRequestId: parseInt(verificationRequestId || "", 10) || 0,
      document: {
        [snakeCase(documentType)]: data || {},
      }
    },
  });

  if (response.ok) {
    return response.data.document;
  }

  throw new Error(response.errorMessage);
};

// saveDocumentData persists a document data payload through the same public
// user-document endpoint the widget calls. Used by the cabinet's CO-driven
// document edits.
export const saveDocumentData = async (
  documentId: string,
  dataId: string,
  jsonData: unknown,
): Promise<any> => {
  const response = await request({
    path: `/api/v1/udoc-service/document/${qe(documentId)}/data`,
    method: "POST",
    body: {
      document_id: parseInt(documentId, 10),
      data_id: dataId,
      json_data: JSON.stringify(jsonData ?? {}),
    },
  });

  if (response.ok) {
    return response.data;
  }

  throw new Error(response.errorMessage);
};

export const createDocument = async (
  documentType: string,
  verificationRequestId: string,
): Promise<TDocumentInfo> => {
  const response = await request({
    path: "/api/v1/udoc-service/document",
    method: "POST",
    body: {
      document_type: documentType,
      verification_request_id: Number(verificationRequestId),
    },
  });

  if (response.ok) {
    return response.data.document;
  }

  throw new Error(response.errorMessage);
};

export const saveDocumentDataForDocument = async (
  document: TDocumentInfo,
  verificationRequestId: string,
  dataId: string,
  jsonData: unknown,
): Promise<TDocumentInfo> => {
  const effectiveDocument = document.documentId
    ? document
    : await createDocument(document.documentType, verificationRequestId);

  await saveDocumentData(effectiveDocument.documentId, dataId, jsonData);
  return effectiveDocument;
};

export const deleteDocumentDataById = async (
  documentDataId: string,
): Promise<any> => {
  const response = await request({
    path: `/api/v1/udoc-service/document-data/${qe(documentDataId)}`,
    method: "DELETE",
  });

  if (response.ok) {
    return response.data;
  }

  throw new Error(response.errorMessage);
};

export type TFileUploadResponse = {
  documentDataId: string;
  dataId: string;
};

export const uploadFormFile = async (
  documentId: string,
  fieldName: string,
  file: File,
  kind?: string,
): Promise<TFileUploadResponse> => {
  const body = new FormData();
  body.append("attachment", file, file.name);
  if (kind) {
    body.append("kind", kind);
  }

  const response = await request({
    path: `/api/v1/udoc-service/document/${qe(documentId)}/form-file/${qe(fieldName)}`,
    method: "POST",
    body,
  });

  if (response.ok) {
    return {
      documentDataId: response.data.documentDataId,
      dataId: response.data.dataId,
    };
  }

  return throwResponseError(response);
};

export const uploadRawDocumentFile = async (
  documentId: string,
  dataId: string,
  file: File,
): Promise<TFileUploadResponse> => {
  const body = new FormData();
  body.append("attachment", file, file.name);

  const response = await request({
    path: `/api/v1/udoc-service/document/${qe(documentId)}/file/${qe(dataId)}`,
    method: "POST",
    body,
  });

  if (response.ok) {
    return {
      documentDataId: response.data.documentDataId,
      dataId: response.data.dataId,
    };
  }

  return throwResponseError(response);
};

export const patchFormFileMetadata = async (
  documentId: string,
  dataId: string,
  metadata: { kind?: string },
): Promise<any> => {
  const response = await request({
    path: `/api/v1/udoc-service/document/${qe(documentId)}/file/${qe(dataId)}/metadata`,
    method: "PATCH",
    body: metadata,
  });

  if (response.ok) {
    return response.data;
  }

  throw new Error(response.errorMessage);
};

export interface ICompanyVerificationStatus {
  status: VerificationStatus;
  displayName: string;
  description: string;
  color: string;
}

export type ICompanyVerificationStatusObject = {
  [key in VerificationStatus]: ICompanyVerificationStatus;
};

export interface ICompanyVerificationStatusesConfig {
  object: ICompanyVerificationStatusObject;
  filters: ICompanyVerificationStatus[];
}

export const getCompanyVerificationStatusList = async (): Promise<
  ICompanyVerificationStatus[]
> => {
  const response = await request({
    path: `/api/v1/verification-service/verification-request/status-list`,
    method: "GET",
  });

  if (response.ok) {
    return response.data.statuses;
  }

  throw new Error(response.errorMessage);
};

export const changeVerificationRequestStatus = async (
  verification_request_id: string,
  action: string,
  additional?: { reason?: string; message?: string }
): Promise<any> => {
  const response = await request({
    path: `/api/v1/verification-service/verification-request/${qe(
      verification_request_id
    )}/status-change`,
    method: "POST",
    body: { action, ...additional },
  });

  if (response.ok) {
    return response.data.verificationRequest;
  }

  throw new Error(response.errorMessage);
};

export const activateScreening = async (
  verification_request_id: string
): Promise<void> => {
  const response = await request({
    path: `/api/v1/verification-service/verification-request/${qe(
      verification_request_id
    )}/activate-screening`,
    method: "POST",
  });

  if (response.ok) {
    return;
  }

  throw new Error(response.errorMessage || "Unable to activate screening");
};

export const changeStatusReasonRequest = async (
  id: string,
  statusReason: string
): Promise<any> => {
  const response = await request({
    path: `/api/v1/verification-service/verification-request/${qe(id)}/status-reason`,
    method: "PATCH",
    body: { statusReason },
  });

  if (response.ok) {
    return response.data;
  }

  throw new Error(response.errorMessage);
};

export const changeRiskLevelRequest = async (
  id: string,
  risk_level: string,
  risk_level_reason?: string
): Promise<any> => {
  const response = await request({
    path: `/api/v1/verification-service/verification-request/${qe(id)}/risk-level`,
    method: "PUT",
    body: { risk_level, risk_level_reason },
  });

  if (response.ok) {
    return response.data;
  }

  throw new Error(response.errorMessage);
};

export const updateDocument = async (data: any): Promise<any> => {
  const response = await request({
    path: `/api/v1/admin-documents/${qe(data.id)}/`,
    method: "PATCH",
    body: data,
  });

  if (response.ok) {
    return response.data;
  }

  throw new Error(response.errorMessage);
};

export interface IVerificationStatusAction {
  name: string;
  displayName: string;
  description: string;
  to: VerificationStatus;
  roles: CompanyRole[];
  conditions: [];
  disabled: boolean;
  color: string;
  requiresReason: boolean;
}

export const getVerificationSatusActions = async (
  verification_request_id: string
): Promise<IVerificationStatusAction[]> => {
  const response = await request({
    path: `/api/v1/verification-service/verification-request/${qe(verification_request_id)}/status-actions`,
    method: "GET",
  });

  if (response.ok) {
    return response.data.actions;
  }

  throw new Error(response.errorMessage);
};

export const getVerificationRequestComments = async (
  verificationId: string
): Promise<{ [key: string]: string }> => {
  const response = await request({
    path: `/api/v1/user-service/verification-request/${qe(verificationId)}/comment`,
    method: "GET",
  });
  if (response.ok) {
    const complianceComments: { [key: string]: string } = {};
    for (let complianceComment of response.data
      .complianceComment as TComplianceComment[]) {
      complianceComments[complianceComment.userDocumentId] =
        complianceComment.comment;
    }
    return complianceComments;
  }

  throw new Error(response.errorMessage);
};

export const addComplianceComment = async (
  verificationId: string,
  userDocumentId: string,
  comment: string
): Promise<{ comment: "string" }> => {
  const response = await request({
    path: `/api/v1/user-service/verification-request/${qe(verificationId)}/comment/document/${qe(userDocumentId)}`,
    method: "PATCH",
    body: { comment },
  });

  if (response.ok) {
    return response.data;
  }

  throw new Error(response.errorMessage);
};

export const getUserById = async (userId: string): Promise<TUserInfo> => {
  const response = await request({
    path: `/api/v1/user-service/user/${qe(userId)}`,
    method: "GET",
  });

  if (response.ok) {
    return response.data.user;
  }

  throw new Error(response.errorMessage);
};

export interface IUserCompanyRoleObject {
  [key: string]: CompanyRole;
}

export const getUserCompanyRole = async (
  authorizedCompanyId: string
): Promise<{
  email: string;
  companyRoleObj: IUserCompanyRoleObject;
  companyRole: CompanyRole;
  isSuperAdmin: boolean;
}> => {
  const response = await request({
    method: "GET",
    path: `/api/v1/user-service/me`,
  });

  if (response.ok) {
    const companyRoleObj = {} as IUserCompanyRoleObject;
    for (let role of response.data?.user?.companyRole as TCompanyRole[]) {
      companyRoleObj[role.companyId] = role.role;
    }
    return {
      email: response.data?.user?.email,
      companyRoleObj,
      companyRole:
        response.data?.user?.companyRole.find(
          (role: any) => role.companyId === authorizedCompanyId
        )?.role ?? CompanyRole.VIEWER,
      isSuperAdmin: response.data?.isSuperAdmin,
    };
  }

  throw new Error(response.errorMessage);
};

export interface IUserProfileInfo {
  userInfo: TUserInfo;
  userCompanies: { items: TCompanyWithRole[] };
  isSuperAdmin: boolean;
}

export const getUserProfileInfo = async (companies: {
  items: TCompany[];
}): Promise<IUserProfileInfo> => {
  const response = await request({
    method: "GET",
    path: `/api/v1/user-service/me`,
  });

  if (response.ok) {
    return {
      userInfo: { ...response.data?.user },
      userCompanies: {
        items: getCompaniesFromIds(
          companies?.items,
          response.data?.user?.companyRole
        ),
      },
      isSuperAdmin: response.data?.isSuperAdmin,
    };
  }

  throw new Error(response.errorMessage);
};

// Screening events network functions
export const getScreeningEvents = async (
  verification_request_id: string
): Promise<TScreeningEvent[]> => {
  const response = await request({
    path: `/api/v1/verification-service/verification-request/${qe(verification_request_id)}/screening-events`,
    method: "GET",
  });

  if (response.ok) {
    return response.data.screeningEvents;
  }

  throw new Error(response.errorMessage);
};

export const getExternalProviderIDs = async (
  verificationRequestId: string,
): Promise<TExternalProviderID[]> => {
  const response = await request({
    path: `/api/v1/verification-service/verification-request/${qe(verificationRequestId)}/external-provider-ids`,
    method: "GET",
  });

  if (response.ok) {
    return response.data.externalIds ?? [];
  }

  throw new Error(response.errorMessage);
};

export const acknowledgeScreeningEvent = async (
  id: string,
  verification_request_id: string
): Promise<void> => {
  const response = await request({
    path: `/api/v1/verification-service/screening-event/${qe(id)}/acknowledge`,
    method: "POST",
    body: { verification_request_id },
  });

  if (response.ok) {
    return;
  }

  throw new Error(response.errorMessage);
};

// Stellar POC
export interface IStellarTxn {
  name: string;
  txnId: string;
  createdAt: string;
}

export interface IStellarInfo {
  stellarAddress: string;
  grants?: Record<string, string>;
  txns?: IStellarTxn[];
}

export const getStellarInfo = async (
  verification_request_id: string
): Promise<IStellarInfo> => {
  const response = await request({
    path: `/api/v1/user-service/stellar-poc/verification-request/${qe(verification_request_id)}`,
    method: "GET",
  });

  if (response.ok) {
    const txns = response.data.txns?.map((txn: any) => ({
      name: txn.name,
      txnId: txn.txn_id || txn.txnId,
      createdAt: txn.created_at || txn.createdAt,
    }));

    return {
      stellarAddress: response.data.stellarAddress || response.data.stellar_address || "",
      grants: response.data.grants || undefined,
      txns: txns || undefined,
    };
  }

  throw new Error(response.errorMessage);
};

export const getVerificationHistory = async (
  verification_request_id: string,
  limit?: number,
  since?: number
): Promise<THistoryResponse> => {
  let path = `/api/v1/verification-service/verification-request/${qe(verification_request_id)}/history`;
  const params = new URLSearchParams();

  if (limit !== undefined) {
    params.set("limit", String(limit));
  }
  if (since !== undefined) {
    params.set("since", String(since));
  }

  const queryString = params.toString();
  if (queryString) {
    path += `?${queryString}`;
  }

  const response = await request({
    path,
    method: "GET",
  });

  if (response.ok) {
    return {
      history: response.data.history || [],
      hasMore: response.data.hasMore || false,
    };
  }

  throw new Error(response.errorMessage);
};

export const downloadHistoryFile = async (
  verificationRequestId: string,
  historyId: string,
  downloadRef: string
): Promise<THistoryFileDownloadResponse> => {
  const response = await request({
    path: `/api/v1/verification-service/verification-request/${qe(
      verificationRequestId
    )}/history/${qe(historyId)}/file/${qe(downloadRef)}`,
    method: "GET",
  });

  if (response.ok) {
    return {
      downloadUrl: response.data?.downloadUrl ?? "",
    };
  }

  throw new Error(response.errorMessage);
};
